// 세금계산서 흐름(billflow) 단일 상태 모델 — 진실의 원천(single source of truth)
// 기존에 컨테이너마다 복제돼 있던 getActiveStep 로직을 이 한 곳으로 통합한다.
// DB 스키마는 그대로 두고, 기존 Y/N 플래그에서 status 를 "파생"한다.

export const BILL_STATUS = {
  // "작성중" 개념은 없다(2026-10-19 요구사항) - 세금계산서는 "발행 예정"만 존재한다.
  // DB 스키마는 그대로 두고(키는 DRAFT 유지) 화면에 보이는 라벨만 통일한다.
  DRAFT: 'DRAFT', // 발행 예정
  ISSUE_REQ: 'ISSUE_REQ', // 발행요청/예정
  ISSUED: 'ISSUED', // 발급완료
  COLLECT_REQ: 'COLLECT_REQ', // 수금/지급 요청
  COLLECTED: 'COLLECTED', // 수금/지급 완료
  CONFIRMED: 'CONFIRMED', // 실적확정
  VOID: 'VOID', // 폐기/비활성 (INV_YN != 'Y')
};

export const STATUS_META = {
  DRAFT: { label: '발행 예정', color: 'default' },
  ISSUE_REQ: { label: '발행요청', color: 'info' },
  ISSUED: { label: '발급완료', color: 'primary' },
  COLLECT_REQ: { label: '수금/지급요청', color: 'secondary' },
  COLLECTED: { label: '수금/지급완료', color: 'success' },
  CONFIRMED: { label: '실적확정', color: 'success' },
  VOID: { label: '폐기', color: 'default' },
};

// 정해진 단계 순서 (필터칩/정렬용)
export const STATUS_ORDER = [
  BILL_STATUS.DRAFT,
  BILL_STATUS.ISSUE_REQ,
  BILL_STATUS.ISSUED,
  BILL_STATUS.COLLECT_REQ,
  BILL_STATUS.COLLECTED,
  BILL_STATUS.CONFIRMED,
];

// 플래그 조합 → 단일 status
export const deriveStatus = (bill) => {
  if (!bill) return BILL_STATUS.DRAFT;
  if (bill.invYn !== 'Y') return BILL_STATUS.VOID;
  if (bill.rsltCompYn === 'Y') return BILL_STATUS.CONFIRMED;
  if (bill.procCompYn === 'Y') return BILL_STATUS.COLLECTED;
  if (bill.procPlanYn === 'Y') return BILL_STATUS.COLLECT_REQ;
  if (bill.reqCompYn === 'Y') return BILL_STATUS.ISSUED;
  if (bill.reqPlanYn === 'Y') return BILL_STATUS.ISSUE_REQ;
  return BILL_STATUS.DRAFT;
};

// 현재 status 에서 가능한 액션만 반환 (disabled 산재 로직 대체)
//  kind: 'forward'(다음 단계) | 'cancel'(되돌리기)
//  dateField: 있으면 호출측에서 오늘 날짜를 payload 에 채워 보낸다.
//  cascade: true 인 취소는 백엔드에서 하위 단계까지 롤백됨(발급취소/수금취소)
/**
 * @param bill
 * @param options.approvalEnabled 결재를 태우는 설정인지(문서종류별 공통코드)
 *
 * "발행요청" 은 결재를 태울 때만 의미가 있는 단계다.
 * 결재를 안 쓰면 요청해봐야 승인해 줄 사람이 없고, 담당자가 두 번 눌러야 해서 번거롭기만 하다.
 * 그래서 결재 미사용이면 [발행] 한 번으로 발행까지 끝낸다.
 */
export const getAvailableActions = (bill, options = {}) => {
  const { approvalEnabled = false, canCancelIssue = true } = options;

  switch (deriveStatus(bill)) {
    case BILL_STATUS.DRAFT:
      return approvalEnabled
        ? [
            { key: 'reqPlan', label: '발행요청', kind: 'forward', payload: { reqPlanYn: 'Y' } },
          ]
        : [
            // 결재가 없으면 요청 단계를 건너뛴다. 예정 플래그도 같이 세워 이력이 비지 않게 한다.
            {
              key: 'issue',
              label: '발행',
              kind: 'forward',
              payload: { reqPlanYn: 'Y', reqCompYn: 'Y' },
              dateField: 'reqCompDt',
            },
          ];
    case BILL_STATUS.ISSUE_REQ:
      return [
        { key: 'reqComp', label: '발급완료', kind: 'forward', payload: { reqCompYn: 'Y' }, dateField: 'reqCompDt' },
        { key: 'reqPlanCancel', label: '발행요청 취소', kind: 'cancel', payload: { reqPlanYn: 'N' } },
      ];
    case BILL_STATUS.ISSUED:
      // 발행과 같은 이유로, 결재를 안 쓰면 "수금 요청" 단계도 승인해 줄 사람이 없어 의미가 없다.
      // 발급이 끝났으면 바로 수금을 잡을 수 있어야 한다.
      // 부분 수금은 이 버튼과 무관하게 [수금 내역]에서 언제든 등록할 수 있다.
      return approvalEnabled
        ? [
            { key: 'procPlan', label: '수금/지급 요청', kind: 'forward', payload: { procPlanYn: 'Y' } },
            {
              key: 'issueCancel',
              label: '발급취소',
              kind: 'cancel',
              cascade: true,
              payload: { reqCompYn: 'N' },
              // 이미 나간 계산서를 되돌리고 아래 단계까지 지운다. 권한자만.
              requiresAuth: !canCancelIssue,
            },
          ]
        : [
            {
              key: 'procComp',
              label: '수금/지급 완료',
              kind: 'forward',
              // 요청 플래그도 같이 세워 이력이 비지 않게 한다.
              payload: { procPlanYn: 'Y', procCompYn: 'Y' },
              dateField: 'procCompDt',
            },
            {
              key: 'issueCancel',
              label: '발급취소',
              kind: 'cancel',
              cascade: true,
              payload: { reqCompYn: 'N' },
              requiresAuth: !canCancelIssue,
            },
          ];
    case BILL_STATUS.COLLECT_REQ:
      return [
        { key: 'procComp', label: '수금/지급 완료', kind: 'forward', payload: { procCompYn: 'Y' }, dateField: 'procCompDt' },
        { key: 'procPlanCancel', label: '수금/지급요청 취소', kind: 'cancel', payload: { procPlanYn: 'N' } },
      ];
    case BILL_STATUS.COLLECTED:
      return [
        { key: 'rsltComp', label: '실적확정', kind: 'forward', payload: { rsltCompYn: 'Y' } },
        { key: 'procCancel', label: '수금/지급취소', kind: 'cancel', cascade: true, payload: { procCompYn: 'N' } },
      ];
    case BILL_STATUS.CONFIRMED:
      return [
        { key: 'rsltCancel', label: '실적취소', kind: 'cancel', payload: { rsltCompYn: 'N' } },
      ];
    default:
      return [];
  }
};

// ── 할 일 관점 필터 ────────────────────────────────────────────────
// 결재를 안 쓰면 "발행요청/수금요청" 단계가 사라져 상태로는 예정 건을 구분할 수 없다.
// 대신 예정일 기준으로 "해야 할 것 / 늦은 것" 을 걸러 본다. 실무에서 필요한 건 단계보다 이쪽이다.

export const TODO_FILTER = {
  ISSUE_DUE: 'ISSUE_DUE',       // 발행할 것 (예정일이 오늘 이후)
  ISSUE_OVERDUE: 'ISSUE_OVERDUE', // 발행 지연
  COLLECT_DUE: 'COLLECT_DUE',   // 수금할 것 (예정일이 오늘 이후)
  COLLECT_OVERDUE: 'COLLECT_OVERDUE', // 수금 지연
};

// "발행 예정"은 이제 DRAFT 상태 라벨이 쓴다(2026-10-19) - 할 일 칩은 "발행일 도래"로 구분한다.
export const TODO_META = {
  ISSUE_DUE: { label: '발행일 도래', color: 'info' },
  ISSUE_OVERDUE: { label: '발행 지연', color: 'error' },
  COLLECT_DUE: { label: '수금/지급 예정', color: 'secondary' },
  COLLECT_OVERDUE: { label: '수금/지급 지연', color: 'error' },
};

const isBefore = (dateStr, baseStr) =>
  Boolean(dateStr) && String(dateStr).slice(0, 10) < baseStr;
const isOnOrAfter = (dateStr, baseStr) =>
  Boolean(dateStr) && String(dateStr).slice(0, 10) >= baseStr;

// 단계는 앞 단계를 품는다 — 수금완료·실적확정이면 발급 체크(reqCompYn)가 N 으로 남아 있어도 이미 발행된 것이다.
// (예전 지급관리 화면이 발급 체크 없이 수금만 세운 건이 있어 플래그 하나만 보면 "발행 예정"에 잘못 잡혔다. 2026-10-10)
const ISSUED_STATUSES = [BILL_STATUS.ISSUED, BILL_STATUS.COLLECT_REQ, BILL_STATUS.COLLECTED, BILL_STATUS.CONFIRMED];
const COLLECTED_STATUSES = [BILL_STATUS.COLLECTED, BILL_STATUS.CONFIRMED];

/**
 * 예정 = 아직 안 했고 예정일이 오늘 이후(오늘 포함), 지연 = 예정일이 지났는데 아직 안 함.
 * 둘은 겹치지 않는다(전에는 "예정"이 지연까지 품어서 두 숫자가 늘 같았다).
 * @param bill
 * @param todo  TODO_FILTER 값
 * @param today 'YYYY-MM-DD'
 */
export const matchesTodo = (bill, todo, today) => {
  if (!bill || !todo) return true;

  const status = deriveStatus(bill);
  if (status === BILL_STATUS.VOID) return false;
  const issued = ISSUED_STATUSES.includes(status);
  const collected = COLLECTED_STATUSES.includes(status);

  switch (todo) {
    case TODO_FILTER.ISSUE_DUE:
      return !issued && isOnOrAfter(bill.reqPlanDt, today);
    case TODO_FILTER.ISSUE_OVERDUE:
      return !issued && isBefore(bill.reqPlanDt, today);
    case TODO_FILTER.COLLECT_DUE:
      return issued && !collected && isOnOrAfter(bill.procPlanDt, today);
    case TODO_FILTER.COLLECT_OVERDUE:
      return issued && !collected && isBefore(bill.procPlanDt, today);
    default:
      return true;
  }
};

// ── 복수 선택 필터 조합 (2026-10-19) ───────────────────────────────
// 상태 칩 줄 / 할 일 칩 줄은 각각 복수 선택이 가능하다.
// 줄 안에서는 OR(아무거나 하나 걸리면 통과), 줄 사이에는 AND.
// 매출/매입 필터는 별도 줄로 항상 AND.
//
// matchesTodo 는 원래부터 invTyp 을 보지 않는다 - 수금/지급 예정·지연 판정은
// 매출/매입 공통 플래그(procPlanDt/procPlanYn/procCompYn)를 그대로 쓰므로
// 매입 세금계산서에도 동일하게 적용된다(대칭). 매출만 세는 것은 헤더 "수금" 뱃지
// (ForecastService#buildCollectCnt, 백엔드)와 그 뱃지를 그대로 반영하는 딥링크
// 진입(?todo=collect) 뿐이다 - 그 경우는 매출 필터를 같이 선택해 재현한다.

// 상태 선택 집합이 비어 있으면(=전체) 모두 통과. 아니면 선택 목록 중 하나라도 걸리면 통과(OR).
export const matchesAnyStatus = (bill, statusSet) => {
  if (!statusSet || statusSet.length === 0) return true;
  return statusSet.includes(deriveStatus(bill));
};

// 할 일 선택 집합이 비어 있으면 모두 통과. 아니면 선택된 할 일 중 하나라도 걸리면 통과(OR).
export const matchesAnyTodo = (bill, todoSet, today) => {
  if (!todoSet || todoSet.length === 0) return true;
  return todoSet.some((todo) => matchesTodo(bill, todo, today));
};

// 매출/매입 필터. 빈 값('')이면 전체 통과.
export const matchesInvTyp = (bill, invTyp) => {
  if (!invTyp) return true;
  return bill?.invTyp === invTyp;
};

/**
 * 세 필터 줄을 한 번에 적용한다(상태 OR, 할 일 OR, 그 둘과 매출/매입은 AND).
 * "내 것" 토글처럼 화면 전용 스코프 필터는 호출측(컨테이너)에서 별도로 더 좁힌다.
 * @param rows 세금계산서 목록
 * @param options.statusSet 선택된 BILL_STATUS 배열 (빈 배열=전체)
 * @param options.todoSet 선택된 TODO_FILTER 배열 (빈 배열=전체)
 * @param options.invTyp '매출' | '매입' | '' (전체)
 * @param options.today 'YYYY-MM-DD' (생략 시 매칭 시점 기준 호출측에서 넘겨줘야 함)
 */
export const filterBillRows = (rows, options = {}) => {
  const { statusSet = [], todoSet = [], invTyp = '', today } = options;
  return (rows || []).filter(
    (bill) =>
      matchesAnyStatus(bill, statusSet) &&
      matchesAnyTodo(bill, todoSet, today) &&
      matchesInvTyp(bill, invTyp),
  );
};
